import { cloneDeep } from 'lodash'

import useBasket from '@/hooks/useBasket'
import usePerson from '@/hooks/usePerson'
import useProduct from '@/hooks/useProduct'

import { isRequired } from '@/utils/CustomErrors'
import {
  getSortedProductsFromGroup,
  getSortedGroupsFromCategory,
  getSortedProductsFromCategory,
  sortProducts,
  updatePriceOptions,
} from '@/utils/Product'
import { SortUtil } from '@/utils/Sorting'

import { CATEGORY_TYPE } from '@/config/constants'
/**
 * @file usePersonSpecificProduct contains logic for products involving a single specific person
 */

// METHODS

/**
 * getGroupsFromCategory
 * @param {string} personId
 * @param {string} categoryId
 * @returns {Category}
 */
function getGroupsFromCategory(personId, categoryId) {
  const { getPerson } = usePerson()
  const __groups = getPerson(personId)?.products?.groups ?? {}

  return getProductGroupsFromCategory(categoryId)
    .filter(group => !!__groups[group.id])
    .map(group => Object.assign(group, __groups[group.id]))
    .sort(SortUtil.sortByOrder)
}

/**
 * getPriceCalculationsForProduct calculates all the price variants for person's product
 * @param {string} productId
 * @param {Basket} payload
 *
 * @returns {Promise<PriceCombination[]>}
 *
 */
async function getPriceCalculationsForProduct(productId, person) {
  const { calculateAllPricesForProduct } = useProduct()
  const { basket } = useBasket()
  const selectedProduct = getProduct(person.personId, productId)
  const selectedPrice = selectedProduct.prices.find(p => p.selected)
  const payload = {
    address: basket.address,
    collective: basket.collective,
    collectivePersonGroup: person.collectivePersonGroup,
    contractNumber: basket.contractNumber,
    contractStartDate: basket.contractStartDate,
    dateOfBirth: person.personData.dateOfBirth,
    gender: person.personData.gender,
    masterTreaty: basket.masterTreaty,
    selectedPriceCombination: selectedPrice,
    zsrNumber: selectedProduct.doctor?.zsrNumber,
  }

  return await calculateAllPricesForProduct(productId, payload)
}

/**
 * getProduct returns the product depending on the personId and productId
 * @param {string} personId
 * @param {string} productId
 * @returns {Product}
 */
function getProduct(personId, productId) {
  const { getPerson } = usePerson()
  return getPerson(personId)?.products?.products[productId]
}

/**
 * getProductPrice returns the price of a product for a given personId and productId
 * @param {string} personId
 * @param {string} productId
 * @returns {Product}
 */
function getProductPrice(personId, productId) {
  return getProduct(personId, productId).prices.find(p => !!p.selected)
}

/**
 * getProductsFromCategory
 * @param {string} categoryId
 * @param {string} personId
 * @returns {}
 */
function getProductsFromCategory(categoryId, personId) {
  const { categories, groups, products } = useProduct()
  const __products = personId ? getPersonProducts(personId) : products.value
  return sortProducts(
    getSortedProductsFromCategory({
      categories: categories.value,
      groups: groups.value,
      products: __products,
      categoryId,
    }),
    __products
  )
}

/**
 * getProductsFromGroup
 * @param {string} groupId
 * @param {string} personId
 * @returns {}
 */
function getProductsFromGroup(groupId, personId) {
  const { groups, products } = useProduct()
  const __products = personId ? getPersonProducts(personId) : products.value

  // pass groups and products from structure, not from the person -> because we need the config-flags from the group/product, e.g. medicalOffice
  return sortProducts(getSortedProductsFromGroup(groups.value, products.value, groupId), __products)
}

/**
 * Groups the selected products of a user by the category
 * and sorts both the products and the categories by order
 * @param {Array} selectedProducts Selected products of a person
 * @return []
 */
function getSelectedProductsByCategory(selectedProducts = isRequired('selected products')) {
  if (selectedProducts.length === 0) return selectedProducts

  const { categories, getCategoryIdFromProduct, getGroupFromProduct } = useProduct()
  const __selectedProducts = cloneDeep(selectedProducts)

  const productsByCategory = {}
  const unknownProducts = []

  Object.values(__selectedProducts).forEach(__product => {
    const categoryId = getCategoryIdFromProduct(__product.productId)
    const group = getGroupFromProduct(__product.productId)

    // add the grouping to the order
    __product.sort = `${group?.order}-${__product.order}`

    categoryId
      ? (productsByCategory[categoryId] = [...(productsByCategory[categoryId] || []), __product])
      : unknownProducts.push(__product)
  })

  unknownProducts.forEach(__product => {
    const categoryId = Object.keys(productsByCategory).find(c =>
      productsByCategory[c].find(pc => pc?.upgrades?.includes(__product.id))
    )
    if (categoryId) {
      // Upgrade products
      productsByCategory[categoryId].push(__product)
    } else {
      // Old products without currently existing category (used for 'Bestandeskunden')
      productsByCategory[CATEGORY_TYPE.UNKONWN] = [...(productsByCategory[CATEGORY_TYPE.UNKONWN] || []), __product]
    }
  })

  Object.keys(productsByCategory).forEach(categoryId => {
    productsByCategory[categoryId].sort(SortUtil.sortByParam('sort'))
  })

  return (
    Object.keys(productsByCategory)
      .map(categoryId => {
        return { id: categoryId, products: productsByCategory[categoryId] }
      })
      /**
       * Get order of category with given categoryId
       * If category not found, return high order number to show this category as last
       * (needed for existing customers with old products)
       */
      .sort(SortUtil.sortByParam(item => Object.values(categories.value).find(c => c.id === item.id)?.order || 1000))
  )
}

/**
 *  getSelectedProductsOfPerson
 *  @todo Replace with selectedProducts on person (everywhere this functin is used, use personWithDetails to get the selectedProducts)
 *  Or even better: add products as param and move it to products util
 *  @param {} personPrducts
 *  @returns {}
 */
function getSelectedProductsOfPerson(personProducts = isRequired('selected products')) {
  const { products } = useProduct()
  const mergeProduct = productId => Object.assign({ productId }, products.value[productId], personProducts[productId])
  const replacePrice = product => {
    product.price = product.prices.find(p => !!p.selected)
    return product
  }

  return Object.keys(personProducts)
    .map(productId => mergeProduct(productId))
    .filter(product => product.selected)
    .map(product => replacePrice(product))
}

/**
 *  hasPersonKVG returns true if a person has at least one KVG product selected
 *  @param {object} __selectedProducts
 *  @returns {boolean}
 */
function hasPersonKVG(__selectedProducts) {
  const productCategories = getSelectedProductsByCategory(__selectedProducts)
  return productCategories.some(category => category.id === CATEGORY_TYPE.KVG)
}

/**
 *  hasPersonOnlyKVG returns true if a person has only selected a KVG product (but no VVG products)
 *  @todo circular dependency with usePerson
 *  @param {string} personId
 *  @returns {boolean}
 */
function hasPersonOnlyKVG(personId) {
  const { getPersonWithDetails } = usePerson()
  const __selectedProducts = getPersonWithDetails(personId).selectedProducts
  const productCategories = getSelectedProductsByCategory(__selectedProducts)
  return productCategories.length === 1 && productCategories[0].id === CATEGORY_TYPE.KVG
}

/**
 *  hasPersonOnlyVVG returns true if a person has only VVG products selected (but no KVG product)
 *  @todo circular dependency with usePerson
 *  @param {string} personId
 *  @returns {boolean}
 */
function hasPersonOnlyVVG(personId) {
  const { getPersonWithDetails } = usePerson()
  const __selectedProducts = getPersonWithDetails(personId).selectedProducts
  const productCategories = getSelectedProductsByCategory(__selectedProducts)
  return productCategories.length > 0 && !productCategories.find(category => category.id === CATEGORY_TYPE.KVG)
}

/**
 *  hasPersonVVG returns true if a person has at least on VVG product selected
 *  @param {object} __selectedProducts
 *  @returns {boolean}
 */
function hasPersonVVG(__selectedProducts) {
  const productCategories = getSelectedProductsByCategory(__selectedProducts)
  return productCategories.some(category =>
    [CATEGORY_TYPE.HOSP, CATEGORY_TYPE.ADDITIONAL, CATEGORY_TYPE.FURTHER_ADDITIONAL].includes(category.id)
  )
}

/**
 *  isProductInBasket
 *  @param {string} personId
 *  @param {string} productId
 *  @returns {boolean}
 */
function isProductInBasket(personId = isRequired('personId'), productId = isRequired('productId')) {
  const { getPerson } = usePerson()
  const person = getPerson(personId)
  return !!person.products.products[productId].selected
}

/**
 *  selectProductOption
 *  @param {{personId: string, productId: string, option: object, __basket: Basket}}
 *  @returns {Promie<Basket>}
 */
async function selectProductOption({ personId, productId, option, __basket = null }) {
  const { updateBasketForPerson } = useBasket()
  const { getPerson } = usePerson()

  const pBasket = __basket ? __basket.persons.find(p => p.personId === personId) : getPerson(personId)
  const product = pBasket.products.products[productId]

  product.prices = updatePriceOptions(product.prices, option)
  return await updateBasketForPerson(personId, pBasket, { basket: __basket })
}

/**
 * selectProductOptionForCategory
 * @param {string} personId
 * @param {string} categoryId
 * @param {} option
 *
 * @returns {Promise<Basket>}
 */
function selectProductOptionForCategory(personId, categoryId, option) {
  const data = {
    products: [],
    mutations: [],
  }

  getGroupsFromCategory(personId, categoryId)
    .reduce((acc, val) => acc.concat(val.products), [])
    .forEach(productId => {
      data.products.push(productId)
      data.mutations.push(selectProductOption({ personId, productId, option }))
    })

  return Promise.all(data.mutations).then(fragments => {
    const __products = fragments.reduce((acc, value, index) => {
      const productId = data.products[index]
      acc[productId] = value.persons.find(p => p.personId === personId).products.products[productId]
      return acc
    }, {})

    const __basket = fragments[0]
    const personIndex = __basket.persons.findIndex(p => p.personId === personId)

    __basket.persons[personIndex].products.products = Object.assign(
      {},
      __basket.persons[personIndex].products.products,
      __products
    )

    return Promise.resolve(__basket)
  })
}

/**
 * transferProductToPerson
 * @param {string} sourceId
 * @param {string} targetId
 * @param {string} productId
 * @param {object} options
 * @returns {Promise<Basket>}
 *
 */
async function transferProductToPerson(sourceId, targetId, productId, options = {}) {
  const { addProductToOvpBasket } = useBasket()
  const { getPerson, persons } = usePerson()
  const __basket = options.basket ?? null
  const mode = options.mode ?? 'REPLACE'
  const doctor = options.doctor ?? null
  const __person = __basket ? persons.value.find(p => p.personId === sourceId) : getPerson(sourceId)
  const product = __person.products.products[productId]
  const option = product.prices.find(p => p.selected)

  const updatedBasket = await addProductToOvpBasket(targetId, productId, { basket: __basket, doctor, mode })
  return await selectProductOption({ personId: targetId, productId, option, __basket: updatedBasket })
}

/**
 *  willReplace
 *  @param {string} personId
 *  @param {string} productId
 *  @returns {}
 */
function willReplace(personId, productId) {
  const { getReplaceableProducts } = useProduct()
  const replaceableProducts = getReplaceableProducts(productId)
  return replaceableProducts.find(__productId => getProduct(personId, __productId)?.selected)
}

// PRIVATE METHODS

/**
 * getPersonProducts
 * @param {string} personId
 * @returns {}
 */
function getPersonProducts(personId) {
  const { getPerson } = usePerson()
  return getPerson(personId)?.products?.products ?? {}
}
/**
 * getProductGroupsFromCategory
 * @param {string} categoryId
 */
function getProductGroupsFromCategory(categoryId) {
  const { categories, groups } = useProduct()
  return getSortedGroupsFromCategory(categories.value, groups.value, categoryId)
}

export default function usePersonSpecificProduct() {
  return {
    // METHODS
    getGroupsFromCategory,
    getPriceCalculationsForProduct,
    getProduct,
    getProductPrice,
    getProductsFromCategory,
    getProductsFromGroup,
    getSelectedProductsByCategory,
    getSelectedProductsOfPerson,
    hasPersonKVG,
    hasPersonOnlyKVG,
    hasPersonOnlyVVG,
    hasPersonVVG,
    isProductInBasket,
    selectProductOption,
    selectProductOptionForCategory,
    transferProductToPerson,
    willReplace,
  }
}
